// ====================================================================================
// COMPONENT: ImageUploadDialog (Aesthetic: Dark Glass & White Accents)
// Self-contained modal with all logic and icons.
// ====================================================================================
import { useState } from "react";
import { UploadCloud, Image as ImageIcon } from "lucide-react";

const ImageUploadDialog = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      console.log('Dropped files:', files);
      alert(`Processing ${files.length} image(s) for upload...`);
      onClose();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      console.log('Selected files:', files);
      alert(`Processing ${files.length} image(s) for upload...`);
      onClose();
    }
  };

  return (
    // Backdrop
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="absolute inset-0" onClick={onClose} />

      {/* Glass Modal Content */}
      <div className="relative bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 p-8 rounded-xl w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <UploadCloud className="text-white" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white leading-none">Upload Images</h2>
            <p className="text-xs text-slate-400 mt-1 font-medium">Add assets to your campaign gallery</p>
          </div>
        </div>

        {/* Dropzone Area */}
        <div
          className={`flex flex-col items-center justify-center p-10 border border-dashed rounded-lg transition-all duration-300 group
            ${isDragOver
              ? 'border-white bg-white/5'
              : 'border-slate-700 bg-slate-950/30 hover:border-slate-500 hover:bg-slate-900/50'
            }`}
          onDragOver={handleDragEnter}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Icon Halo */}
          <div className="w-12 h-12 rounded-full bg-slate-900 border border-slate-700 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg group-hover:border-white/30">
            <ImageIcon size={20} className="text-slate-400 group-hover:text-white transition-colors" />
          </div>

          <p className="text-slate-300 font-bold text-sm mb-1">Drag & Drop images here</p>
          <p className="text-[10px] text-slate-500 mb-5 uppercase tracking-wide font-bold">PNG, JPG, SVG • Max 10MB</p>

          <label htmlFor="file-upload" className="cursor-pointer bg-slate-800 hover:bg-white hover:text-slate-900 text-white text-xs font-bold uppercase tracking-wider px-6 py-2.5 rounded-lg border border-slate-600 hover:border-white transition-all shadow-lg hover:shadow-xl">
            Browse Files
          </label>
          <input id="file-upload" type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageUploadDialog;
